import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Permissions } from '@app/core/guard/Permissions';
import { PermissionsService } from '@services/helpers/permissions.service';
import { MeetingDto } from '@interfaces/meetings/responses/meeting.response';
import { DateFormatterService } from '@services/helpers/date-formatter.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { MeetingsHttpService } from '@services/http/meetings/meetings-http.service';
import { SharedModule } from '@src/app/shared/shared.module';
import { finalize, first } from 'rxjs';
import { mensagemDoErro } from '@features/dashboard/meetings/components/meeting-presenter';
import {
  displayTitle,
  originalTitleIfDifferent,
  renamedByText,
} from '@features/dashboard/meetings/components/meeting-visibility-presenter';

/** Limite do título dado por uma pessoa, igual ao do backend (`PUT meetings/{id}/title`). */
export const MEETING_TITLE_MAX = 200;

/**
 * Título da call no detalhe, com o lápis de renomear (card 3803, opção C do protótipo).
 *
 * - Mostra o título exibido, a descrição curta do Cog, "Título original: …" (quando difere) e
 *   "Renomeada por X em DD/MM".
 * - O lápis só aparece para quem pode (`canEdit`: visão geral e `visibility.canManage`). Editando:
 *   **Salvar**, **Cancelar** e **Usar o sugerido pelo Cog** (quando há sugestão diferente). Salvar vazio
 *   volta ao automático (sugerido pelo Cog, senão o do convite): quem decide é o backend.
 * - Chama a API e devolve a reunião atualizada em `renamed`.
 */
@Component({
  selector: 'app-meeting-title-editor',
  standalone: true,
  imports: [SharedModule, FormsModule],
  templateUrl: './meeting-title-editor.component.html',
  styleUrl: './meeting-title-editor.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeetingTitleEditorComponent {
  readonly meeting = input.required<MeetingDto>();
  /** Quem está vendo pode renomear (visão geral e participante ou admin interno). */
  readonly canEdit = input(false);

  readonly renamed = output<MeetingDto>();

  /** `MEETINGVISIBILITY_RENAME` de quem está logado (regra de permissão por ação). */
  private readonly hasRenamePermission = toSignal(
    inject(PermissionsService).hasPermission(Permissions.MeetingVisibility.RENAME),
    { initialValue: false }
  );
  /** O lápis aparece com as duas: a regra do backend (`canEdit`) e a permissão da ação. */
  protected readonly showPencil = computed(() => this.canEdit() && this.hasRenamePermission());

  protected readonly maxLength = MEETING_TITLE_MAX;
  protected readonly editing = signal(false);
  protected readonly draft = signal('');
  protected readonly saving = signal(false);

  protected readonly title = computed(() => displayTitle(this.meeting()));
  protected readonly originalTitle = computed(() => originalTitleIfDifferent(this.meeting()));

  protected readonly renamedBy = computed(() => {
    const m = this.meeting();
    const on = m.customTitleAt ? this.dates.formatDateTimeInFormat(m.customTitleAt, 'dd/MM') : null;
    return renamedByText(m, on);
  });

  /** Sugestão do Cog para o atalho, só quando difere do que está no campo. */
  protected readonly suggestedTitle = computed(() => {
    const suggested = this.meeting().suggestedTitle?.trim();
    return suggested && suggested !== this.draft().trim() ? suggested : null;
  });

  protected readonly canSave = computed(
    () => !this.saving() && this.draft().trim().length <= MEETING_TITLE_MAX
  );

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly meetingsService: MeetingsHttpService,
    private readonly toast: ToastHelperService,
    private readonly dates: DateFormatterService
  ) {}

  protected startEditing(): void {
    this.draft.set(this.meeting().customTitle ?? this.title());
    this.editing.set(true);
  }

  protected cancel(): void {
    this.editing.set(false);
  }

  protected useSuggested(): void {
    const suggested = this.meeting().suggestedTitle;
    if (suggested) this.draft.set(suggested);
  }

  /** Grava o título; vazio volta ao automático. */
  protected save(): void {
    if (!this.canSave()) return;
    const title = this.draft().trim() || null;
    this.saving.set(true);
    this.meetingsService
      .renameMeeting(this.meeting().id, title)
      .pipe(
        first(),
        finalize(() => this.saving.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: updated => {
          this.editing.set(false);
          this.toast.showToastSuccess(
            title
              ? 'Call renomeada. O título original continua guardado.'
              : 'A call voltou ao título automático.'
          );
          if (updated) this.renamed.emit(updated);
        },
        error: error =>
          this.toast.showToastError(
            'Não foi possível renomear a call',
            mensagemDoErro(error, 'Tente de novo em alguns instantes.')
          ),
      });
  }
}
